import {
    useCallback,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

const InstallPromptContext = createContext(null);
const INSTALLED_STORAGE_KEY = 'inksu-pwa-installed';

function isStandaloneMode() {
    if (typeof window === 'undefined') {
        return false;
    }

    const matchesDisplayMode =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(display-mode: standalone)').matches;

    return (
        matchesDisplayMode || window.navigator.standalone === true
    );
}

function isIOSDevice() {
    if (typeof window === 'undefined') {
        return false;
    }

    const userAgent = window.navigator.userAgent || '';
    const platform = window.navigator.platform || '';
    const isTouchMac =
        platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;

    return /iPad|iPhone|iPod/i.test(userAgent) || isTouchMac;
}

function readInstalledState() {
    if (typeof window === 'undefined') {
        return false;
    }

    try {
        return window.localStorage.getItem(INSTALLED_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
}

function storeInstalledState(installed) {
    try {
        if (installed) {
            window.localStorage.setItem(INSTALLED_STORAGE_KEY, 'true');
        } else {
            window.localStorage.removeItem(INSTALLED_STORAGE_KEY);
        }
    } catch {
        // La detección standalone sigue funcionando si el almacenamiento falla.
    }
}

export function InstallPromptProvider({ children }) {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isStandalone, setIsStandalone] = useState(isStandaloneMode);
    const [isInstalled, setIsInstalled] = useState(
        () => isStandaloneMode() || readInstalledState()
    );
    const [isPrompting, setIsPrompting] = useState(false);
    const isIOS = isIOSDevice();

    useEffect(() => {
        const mediaQuery =
            typeof window.matchMedia === 'function'
                ? window.matchMedia('(display-mode: standalone)')
                : null;

        const syncStandaloneState = () => {
            const standalone = isStandaloneMode();
            setIsStandalone(standalone);

            if (standalone) {
                setIsInstalled(true);
                storeInstalledState(true);
            }
        };

        const handleBeforeInstallPrompt = (event) => {
            event.preventDefault();
            setDeferredPrompt(event);
            setIsInstalled(false);
            storeInstalledState(false);
        };

        const handleAppInstalled = () => {
            setDeferredPrompt(null);
            setIsInstalled(true);
            setIsPrompting(false);
            storeInstalledState(true);
        };

        syncStandaloneState();
        window.addEventListener(
            'beforeinstallprompt',
            handleBeforeInstallPrompt
        );
        window.addEventListener('appinstalled', handleAppInstalled);

        if (mediaQuery?.addEventListener) {
            mediaQuery.addEventListener('change', syncStandaloneState);
        } else if (mediaQuery?.addListener) {
            mediaQuery.addListener(syncStandaloneState);
        }

        return () => {
            window.removeEventListener(
                'beforeinstallprompt',
                handleBeforeInstallPrompt
            );
            window.removeEventListener('appinstalled', handleAppInstalled);

            if (mediaQuery?.removeEventListener) {
                mediaQuery.removeEventListener('change', syncStandaloneState);
            } else if (mediaQuery?.removeListener) {
                mediaQuery.removeListener(syncStandaloneState);
            }
        };
    }, []);

    const requestInstall = useCallback(async () => {
        if (isStandalone || isInstalled) {
            return { status: 'installed' };
        }

        if (isIOS) {
            return { status: 'ios' };
        }

        if (!deferredPrompt) {
            return { status: 'unavailable' };
        }

        setIsPrompting(true);

        try {
            await deferredPrompt.prompt();
            const choice = await deferredPrompt.userChoice;
            setDeferredPrompt(null);

            if (choice.outcome === 'accepted') {
                return { status: 'accepted' };
            }

            return { status: 'dismissed' };
        } catch {
            setDeferredPrompt(null);
            return { status: 'unavailable' };
        } finally {
            setIsPrompting(false);
        }
    }, [deferredPrompt, isIOS, isInstalled, isStandalone]);

    const value = useMemo(
        () => ({
            canPrompt: Boolean(deferredPrompt),
            isIOS,
            isInstalled,
            isPrompting,
            isStandalone,
            requestInstall,
            shouldShow: !isStandalone && !isInstalled,
        }),
        [
            deferredPrompt,
            isIOS,
            isInstalled,
            isPrompting,
            isStandalone,
            requestInstall,
        ]
    );

    return (
        <InstallPromptContext.Provider value={value}>
            {children}
        </InstallPromptContext.Provider>
    );
}

export function useInstallPrompt() {
    const context = useContext(InstallPromptContext);

    if (!context) {
        throw new Error(
            'useInstallPrompt debe usarse dentro de InstallPromptProvider'
        );
    }

    return context;
}
